import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  LoginUserBody,
  LoginUserData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface LoginUserState
  extends MergeRequestState<LoginUserData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.loginUser,
  isFetching: (state: ReduxState) => state.server.loginUser.isFetching,
  data: (state: ReduxState) => state.server.loginUser.data,
  error: (state: ReduxState) => state.server.loginUser.error,
};

const initialState: LoginUserState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/loginUser';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: LoginUserBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.loginUser(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerLoginUser {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const loginUser: ServerLoginUser = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
